// --- React
import React from 'react';
// --- UI Components
import { DetailsModal } from '../../DetailsModal';
import { ViewCommentsForm } from './view-form';
import Button from '@dtci/spellbook/Button';
// --- Redux
import { useSelector } from 'react-redux';
import { useCommentsActions } from '../../../store/comments/useComments';
import { useProductsActions } from '../../../store/products/useProducts';
// --- API
import { datastageApi } from '../../../api';
// --- Utilities
import { filterCriteria } from '../../../utils/filterCriteria';
// --- Hooks
import { useLoading } from '../../../utils/hooks/useLoading';
import { getItemRange } from '../../../utils/pagination';
import { Pagination } from '../../Pagination';
import './styles.scss';
import {updateClearanceMemoTitles} from "../../../store/clearanceMemoTitles/actions";

/**
 * ViewCommentsModal Component
 *
 * This component is responsible for rendering the modal used to view comments.
 * It includes a form for displaying comment details and provides an option to close the modal.
 * It also handles the logic for fetching the updated comments data when the modal is closed.
 *
 * @component
 * @returns {JSX.Element} The rendered ViewCommentsModal component.
 */
export function ViewCommentsModal() {
    const {
        isCommentModalOpen,
        commentProduct,
        totalPages,
        currentPage,
        itemsPerPage,
        totalItems,
        commentSort,
        isClearancesComment
    } = useSelector((s) => s.comments);
    const { filterForm, productRange, productSort } = useSelector((s) => s.products);
    const { filterForm: clearancesFilterForm, titleRange, titleSort } = useSelector((s) => s.titles);
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { clearComments, updateComments } = useCommentsActions();
    const { updateProducts } = useProductsActions();

    // Function to handle the close action of the view comments modal
    const onClose = async () => {
        clearComments();
        setLoadingTrue();
        if (isClearancesComment) {

            const data = await datastageApi.clearanceMemoTitles(
                filterCriteria(clearancesFilterForm, '', 'clearanceMemoTitles'),
                titleRange,
                titleSort,
            );

            updateClearanceMemoTitles({
                titles: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable.pageNumber,
            });

        } else {
            const data = await datastageApi.products(
                filterCriteria(filterForm, '', 'products'),
                productRange,
                productSort,
            );

            updateProducts({
                products: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable.pageNumber,
            });
        }

        setLoadingFalse();
    };

    // Function to handle the pagination of the comments
    const handlePageChange = async (pageNumber) => {
        if (pageNumber !== currentPage) {
            setLoadingTrue();
            const nextPageRange = getItemRange(pageNumber, itemsPerPage, totalItems);
            const data = await datastageApi.comments(
                filterCriteria(filterForm, commentProduct, 'comments'),
                nextPageRange,
                commentSort,
            );

            updateComments({
                comments: data?.content,
                totalItems: data?.totalElements,
                totalPages: data?.totalPages,
                currentPage: data?.pageable.pageNumber,
            });
            setLoadingFalse();
        }
    };

    return (
        <DetailsModal
            isOpen={isCommentModalOpen}
            onClose={onClose}
            modalHeader="Comments"
            footer={
                <div className="view-comments-footer">
                    <div className="view-comments-pagination">
                        <Pagination
                            pageCount={totalPages}
                            onPageChange={(e) => handlePageChange(e.selected)}
                            previousLabel="←"
                            nextLabel="→"
                            forcePage={currentPage}
                        />
                    </div>
                    <div>
                        <Button
                            sizeType="small"
                            styleType="link"
                            iconType="fa-solid fa-close"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            }
        >
            <ViewCommentsForm />
        </DetailsModal>
    );
}
